// Legacy module name kept for backwards-compat with ~30 importers.
// Shopify has been removed from the runtime. These functions read from
// Lovable Cloud (Postgres) with anon RLS and return data in the shape
// the rest of the app already consumes.
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import magHero__asset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
const magHero = magHero__asset.url;
import liverHero__asset from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
const liverHero = liverHero__asset.url;
import ovaHero__asset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
const ovaHero = ovaHero__asset.url;

const HERO_BY_HANDLE: Record<string, string> = {
  "mag-absorb-pro": magHero,
  "liver-biome-plus": liverHero,
  "ova-balance": ovaHero,
};

const PRODUCT_TYPE_BY_HANDLE: Record<string, string> = {
  "mag-absorb-pro": "Supplement",
  "liver-biome-plus": "Supplement",
  "ova-balance": "Supplement",
};

const TAGS_BY_HANDLE: Record<string, string[]> = {
  "mag-absorb-pro": ["goal-sleep", "goal-stress", "goal-recovery"],
  "liver-biome-plus": ["goal-detox", "goal-gut", "goal-immunity"],
  "ova-balance": ["goal-hormones", "goal-pcos", "goal-mood"],
};

// Shape mirrors the Shopify Storefront GraphQL fragment used throughout the app.
export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    productType: string;
    tags: string[];
    priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
    compareAtPriceRange?: { minVariantPrice: { amount: string; currencyCode: string } };
    images: { edges: Array<{ node: { url: string; altText: string | null } }> };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: { amount: string; currencyCode: string };
          compareAtPrice?: { amount: string; currencyCode: string } | null;
          availableForSale: boolean;
          selectedOptions: Array<{ name: string; value: string }>;
        };
      }>;
    };
  };
}

// Hidden handles filter (internal test SKUs).
const HIDDEN_HANDLES = new Set<string>(["truwe-checkout-test-product-1"]);
function isHidden(handle: string): boolean {
  if (HIDDEN_HANDLES.has(handle)) return true;
  return /(^|-)test(-|$)/i.test(handle);
}

// Isomorphic Supabase client:
//   - browser: reuse the persisted anon client so we don't spin up a
//     second WS/session listener.
//   - server (SSR, server fns, server routes): a fresh anon client using
//     SUPABASE_URL / SUPABASE_PUBLISHABLE_KEY, no session persistence.
let cachedServerClient: SupabaseClient<Database> | null = null;
async function getClient(): Promise<SupabaseClient<Database>> {
  if (typeof window !== "undefined") {
    const mod = await import("@/integrations/supabase/client");
    return mod.supabase as SupabaseClient<Database>;
  }
  if (cachedServerClient) return cachedServerClient;
  const url = process.env.SUPABASE_URL || "";
  const key = process.env.SUPABASE_PUBLISHABLE_KEY || "";
  if (!url || !key) {
    throw new Error("SUPABASE_URL / SUPABASE_PUBLISHABLE_KEY missing on server");
  }
  cachedServerClient = createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        // New-format sb_publishable_ keys are not JWTs; drop the bearer.
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
          h.delete("Authorization");
        }
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
  return cachedServerClient;
}

function paiseToAmount(paise: number | null | undefined): string {
  return ((Number(paise) || 0) / 100).toFixed(2);
}

function toShapedProduct(row: any): ShopifyProduct["node"] {
  const handle = String(row.handle);
  const rawImages = Array.isArray(row.images) ? row.images : [];
  const heroFallback = HERO_BY_HANDLE[handle];
  const imageUrls: string[] = [
    ...(row.image_url ? [row.image_url] : []),
    ...rawImages,
    ...(heroFallback ? [heroFallback] : []),
  ].filter((v, i, a) => v && a.indexOf(v) === i);

  const variants: any[] = Array.isArray(row.variants) ? row.variants : [];
  variants.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const activeVariants = variants.filter((v) => v.active !== false);
  const effectiveVariants = activeVariants.length ? activeVariants : variants;

  // availableForSale is reported true regardless of inventory — real stock
  // is enforced at checkout (orders.server decrements the inventory row)
  // and SOLD_OUT in src/lib/storeStatus.ts is the pre-launch master gate.
  const shapedVariants = effectiveVariants.map((v) => ({
    node: {
      // Cart lines & order resolution key off variants.shopify_id (legacy
      // GID). Fall back to the row uuid if that column is null.
      id: String(v.shopify_id || v.id),
      title: String(v.title || "Default"),
      price: { amount: paiseToAmount(v.price_paise), currencyCode: "INR" },
      compareAtPrice: v.compare_at_paise
        ? { amount: paiseToAmount(v.compare_at_paise), currencyCode: "INR" }
        : null,
      availableForSale: true,
      selectedOptions: [{ name: "Title", value: String(v.title || "Default") }],
    },
  }));

  const primary = effectiveVariants[0] || {};
  const minPricePaise = effectiveVariants.reduce(
    (m: number, v: any) => Math.min(m, Number(v.price_paise) || 0),
    Number(primary.price_paise) || 0,
  );
  const compareAtMin = effectiveVariants.reduce((m: number | null, v: any) => {
    const val = v.compare_at_paise ? Number(v.compare_at_paise) : null;
    if (val == null) return m;
    return m == null ? val : Math.min(m, val);
  }, null as number | null);

  return {
    id: String(row.id),
    title: String(row.title || handle),
    description: String(row.description || ""),
    handle,
    productType: PRODUCT_TYPE_BY_HANDLE[handle] || "Supplement",
    tags: TAGS_BY_HANDLE[handle] || [],
    priceRange: {
      minVariantPrice: { amount: paiseToAmount(minPricePaise), currencyCode: "INR" },
    },
    ...(compareAtMin != null
      ? {
          compareAtPriceRange: {
            minVariantPrice: { amount: paiseToAmount(compareAtMin), currencyCode: "INR" },
          },
        }
      : {}),
    images: {
      edges: imageUrls.map((url) => ({ node: { url, altText: String(row.title || handle) } })),
    },
    variants: { edges: shapedVariants },
  };
}

const PRODUCT_SELECT =
  // NOTE: inventory is intentionally NOT selected — the table is admin-only and
  // availableForSale is always reported true (stock is enforced at checkout).
  "id, handle, title, description, image_url, images, status, variants(id, shopify_id, title, price_paise, compare_at_paise, position, active)";

export async function fetchProducts(first = 24): Promise<ShopifyProduct[]> {
  try {
    const supabase = await getClient();
    const { data, error } = await supabase
      .from("products")
      .select(PRODUCT_SELECT)
      .eq("status", "active")
      .lte("publish_at", new Date().toISOString())
      .order("title")
      .limit(first);
    if (error) {
      console.warn("[products] fetchProducts error", error.message);
      return [];
    }
    return (data ?? [])
      .map((r) => ({ node: toShapedProduct(r) }))
      .filter((e) => !isHidden(e.node.handle));
  } catch (err) {
    console.warn("[products] fetchProducts failed", (err as Error).message);
    return [];
  }
}

export async function fetchProductByHandle(
  handle: string,
): Promise<ShopifyProduct["node"] | null> {
  if (isHidden(handle)) return null;
  try {
    const supabase = await getClient();
    const { data, error } = await supabase
      .from("products")
      .select(PRODUCT_SELECT)
      .eq("handle", handle)
      .eq("status", "active")
      .lte("publish_at", new Date().toISOString())
      .maybeSingle();
    if (error) {
      console.warn("[products] fetchProductByHandle error", error.message);
      return null;
    }
    return data ? toShapedProduct(data) : null;
  } catch (err) {
    console.warn("[products] fetchProductByHandle failed", (err as Error).message);
    return null;
  }
}

// Announcement bar used to read a Shopify metafield. No Shopify now —
// return null so AnnouncementBar falls back to its baked-in defaults.
export async function fetchAnnouncementMessages(): Promise<string[] | null> {
  return null;
}
